import { useEffect, useRef, useState } from 'react';
import { useBlocker, type BlockerFunction } from 'react-router';

import { Button } from '@/components/book';
import { anyDirty, flushAll, takeExcuse } from '@/lib/editGuard/store';
import styles from '@/app/EditGuard.module.css';

const FLUSH_MS = 1_500;

/** A move to another page while an edit is unsaved; a fragment or a cleared parameter on the same page is not leaving it. */
const leaving: BlockerFunction = ({ currentLocation, nextLocation }) =>
  currentLocation.pathname !== nextLocation.pathname && !takeExcuse() && anyDirty();

/**
 * Asks before leaving a page whose edits are not saved (WQ13). It first sends
 * what is queued and waits up to 1.5 s; when that saves, the move goes ahead
 * without asking. Otherwise: *Changes not saved.* · *Stay* · *Leave anyway*.
 * Mounted once inside the data router; every in-app move (links, keys, the
 * rail, Back and Forward, an entity link in the editor) passes it.
 */
export function EditGuard() {
  const blocker = useBlocker(leaving);
  const [asking, setAsking] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const blocked = blocker.state === 'blocked';

  useEffect(() => {
    if (!blocked) {
      setAsking(false);
      return;
    }
    let live = true;
    void flushAll(FLUSH_MS).then((saved) => {
      if (!live) return;
      // The flush's own answer, not the reported states: those catch up only after the next draw.
      if (saved) blocker.proceed?.();
      else setAsking(true);
    });
    return () => {
      live = false;
    };
    // Once per blocked move; `blocker` is a new object on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocked]);

  useEffect(() => {
    const el = dialog.current;
    if (asking && el && !el.open) el.showModal?.();
  }, [asking]);

  if (!blocked || !asking) return null;
  return (
    <dialog ref={dialog} className={styles.dialog} aria-labelledby="edit-guard-h" onCancel={(e) => { e.preventDefault(); blocker.reset?.(); }}>
      <p id="edit-guard-h" className={styles.head}>Changes not saved.</p>
      <div className={styles.actions}>
        <Button autoFocus onClick={() => blocker.reset?.()}>Stay</Button>
        <Button onClick={() => blocker.proceed?.()}>Leave anyway</Button>
      </div>
    </dialog>
  );
}
